import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';

export enum questionType {
  CODING = 'Coding',
  OBJECTIVE = 'Objective',
  VIDEO = 'Subjective Video',
  SUBJECTIVE = 'Subjective Video',
  TEXTUAL = 'Subjective Text',
}

export interface language {
  name: string;
  id: number;
  onlineJudgeId: number;
  monaco_id: string;
}

interface IVideoQuestionUpdateType {
  attemptLeft: number;
  lastAttempt: null | {
    video: string;
    transcription: string;
  };
  storageData: null | {
    location: string;
    env: 'HH_API' | null;
  };
  maxTimeAllowed: number;
  lastAttemptSubmitted: boolean;
}

export interface testcase {
  test_case_id: string;
  input: string;
  output: string;
  score: string;
}

export interface QuestionModal {
  question_id: string;
  index: number;
  title: string;
  description: string;
  choice: { option: string; value: string }[];
  answer: string;
  time_taken: number;
  start_time: string;
  langauges: language[];
}

export interface code {
  tail: string;
  head_body: string;
}

export interface QuestionState {
  question_id: string;
  index: number;
  title: string;
  description: string;
  choice: { option: string; value: string }[];
  answer: string;
  time_taken: number;
  start_time: string;
  question_type: questionType;
  code?: code;
  defaultCode?: code;
  test_cases?: testcase[];
  coding_languages?: language[];
  attemptLeft: number;
  lastAttempt: null | {
    video: string;
    transcription: string;
  };
  attendee_id: number | undefined;
  storageData: null | {
    location: string;
    env: 'HH_API' | null;
  };
  maxTimeAllowed: number;
  lastAttemptSubmitted: boolean;
  videoQuestionState: VideoQuestionState;
}

export interface testcase {
  test_case_id: string;
  input: string;
  output: string;
  score: string;
  is_sample: boolean;
}

export enum VideoQuestionState {
  QUESTION_NOT_VISIBLE = 'QUESTION_NOT_VISIBLE',
  QUESTION_VISIBLE = 'QUESTION_VISIBLE',
  SKIPPED = 'SKIPPED',
  SUBMITTED = 'SUBMITTED',
}

const initialState: QuestionState = {
  question_id: '',
  question_type: questionType.CODING,
  index: 0,
  title: '',
  description: '',
  choice: [
    {
      option: 'a',
      value: '',
    },
    {
      option: 'b',
      value: '',
    },
    {
      option: 'c',
      value: '',
    },
    {
      option: 'd',
      value: '',
    },
  ],
  answer: '',
  time_taken: 0,
  start_time: moment().unix().toString(),
  attemptLeft: 0,
  lastAttempt: null,
  storageData: null,
  maxTimeAllowed: 10,
  lastAttemptSubmitted: false,
  videoQuestionState: VideoQuestionState.QUESTION_NOT_VISIBLE,
  attendee_id: undefined,
};

export const questionSlice = createSlice({
  name: 'assessment',
  initialState,
  reducers: {
    setQuestionInfo: (state, action: PayloadAction<QuestionState>) => {
      const {
        question_id,
        index,
        title,
        description,
        choice,
        answer,
        time_taken,
        code,
        defaultCode,
        test_cases,
        coding_languages,
        question_type,
        attemptLeft = 0,
        lastAttempt = null,
        storageData = null,
        maxTimeAllowed = 10,
        lastAttemptSubmitted,
        attendee_id = undefined,
        videoQuestionState = VideoQuestionState.QUESTION_NOT_VISIBLE,
      } = action.payload;
      state.attemptLeft = attemptLeft;
      state.lastAttempt = lastAttempt;
      state.storageData = storageData;
      state.maxTimeAllowed = maxTimeAllowed;
      state.lastAttemptSubmitted = lastAttemptSubmitted;
      state.question_id = question_id;
      state.index = index;
      state.title = title;
      state.description = description;
      state.choice = choice;
      state.answer = answer;
      state.time_taken = time_taken;
      state.code = code;
      state.defaultCode = defaultCode;
      state.question_type = question_type;
      state.coding_languages = coding_languages;
      state.test_cases = test_cases;
      state.videoQuestionState = videoQuestionState;
      state.attendee_id = attendee_id;
    },
    setQuestionAnswer: (state, action: PayloadAction<string>) => {
      state.answer = action.payload;
    },
    setQuestionStartTime: (state, action: PayloadAction<string>) => {
      state.start_time = action.payload;
    },
    setVideoQuestionUpdate: (
      state,
      action: PayloadAction<IVideoQuestionUpdateType>
    ) => {
      const {
        attemptLeft,
        lastAttempt,
        storageData,
        maxTimeAllowed,
        lastAttemptSubmitted,
      } = action.payload;
      state.attemptLeft = attemptLeft;
      state.lastAttempt = lastAttempt;
      state.storageData = storageData;
      state.maxTimeAllowed = maxTimeAllowed;
      state.lastAttemptSubmitted = lastAttemptSubmitted;
    },
  },
});

export const {
  setQuestionInfo,
  setQuestionAnswer,
  setQuestionStartTime,
  setVideoQuestionUpdate,
} = questionSlice.actions;

export default questionSlice.reducer;
